<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-4">
      <JadeInputField
        size="medium"
        :value.sync="id"
        :placeholder="$t('placeholder')"
        :leadingItem="{ type: 'icon', content: 'action-search' }"
        label=""
      >
      </JadeInputField>
      <JadeButton
        variant="primary-solid"
        size="medium"
        :disabled="loading"
        @click="searchClick"
        class="w-20"
      >
        {{ $t('search') }}
      </JadeButton>
      <JadeIconButton
        icon="action-refresh"
        variant="neutral-ghost"
        size="medium"
        @click="refreshClick"
      />
    </div>
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-4">
        <JadeDropdownSelection
          type="multi"
          searchable
          :allowedPlacements="['bottom-start']"
          :options="statusFilterOptions"
          @onApply="applyFilterStatus"
          @onClear="clearFiltersSearch"
        >
          <template #trigger>
            <JadeActionButton
              :disabled="loading"
              :active="!!status"
              dropdownIndicator
            >
              {{ $t(statusToString || 'status') }}
            </JadeActionButton>
          </template>
        </JadeDropdownSelection>
        <JadeButton
          v-if="hasFilter"
          variant="ghost"
          size="small"
          @click="clearFiltersSearch"
        >
          {{ $t('clear-filters') }}
        </JadeButton>
      </div>
      <JadeActionButton
        icon="action-download"
        :disabled="loading"
        @click="exportClick"
        class="max-w-40"
      >
        <JadeText weight="semibold">
          {{ $t('download') }}
        </JadeText>
      </JadeActionButton>
    </div>
    <JadeToastHub ref="toast" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { CHARGEBACK_STATUS_ENUM } from '@/enums/chargeback.enum'
import {
  JadeButton,
  JadeText,
  JadeToastHub,
  JadeDropdownSelection,
  JadeInputField,
  JadeActionButton,
  JadeIconButton
} from '@jade'

import downloadBlob from '@/utils/download.utils'

const initialValues = Object.values(CHARGEBACK_STATUS_ENUM).map(value => ({
  label: value.label,
  selected: false,
  value: value.key
}))

export default {
  name: 'ChargebackSearch',
  components: {
    JadeInputField,
    JadeButton,
    JadeIconButton,
    JadeActionButton,
    JadeToastHub,
    JadeDropdownSelection,
    JadeText
  },
  data () {
    return {
      statusFilterOptions: initialValues
    }
  },
  props: {
    status: Array,
    transactionId: String
  },
  computed: {
    ...mapGetters({ loading: 'loadingChargebacks' }),
    hasFilter () {
      return !!this.status
    },
    statusLabels () {
      return Object.values(CHARGEBACK_STATUS_ENUM)
        .filter(item => this.status?.includes(item.key))
        .map(item => item.label)
    },
    statusToString () {
      return this.statusLabels?.join(', ')
    }
  },
  watch: {
    status: {
      handler (status) {
        if (!status) return
        const tempArray = this.statusFilterOptions
        status.forEach(value => {
          const indexOf = tempArray.findIndex(item => item.value === value)
          tempArray[indexOf].selected = true
        })
        this.statusFilterOptions = tempArray
      },
      deep: true
    },
    transactionId: {
      handler (id) {
        this.id = id
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions({ exportCsv: 'EXPORT_CHARGEBACK_CSV' }),
    exportClick () {
      this.$refs.toast.addToast({
        text: this.$t('csv.loading'),
        loading: true
      })

      this.exportCsv({ page: this.page })
        .then(text => {
          const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
          downloadBlob(blob, 'chargebacks.csv')

          this.$refs.toast.addToast({
            text: this.$t('csv.success'),
            icon: 'action-download'
          })
        })
        .catch(() => {
          this.$refs.toast.addToast({
            text: this.$t('csv.error'),
            icon: 'circle-error'
          })
        })
    },
    searchClick () {
      this.$emit('searchClick', this.id)
    },
    refreshClick () {
      this.$emit('refreshClick')
    },
    clearFiltersSearch () {
      this.statusFilterOptions = this.statusFilterOptions.map(option => ({
        ...option,
        selected: false
      }))
      this.id = ''
      this.$emit('selectedStatus', undefined)
    },
    applyFilterStatus (status) {
      if (status?.length === 0) return

      this.$emit(
        'selectedStatus',
        status.map(status => status.value)
      )
    }
  }
}
</script>

<i18n>
  {
    "pt-BR": {
      "search": "Buscar",
      "batch-dispute": "Disputar em lote",
      "placeholder": "Buscar por ID",
      "download": "Baixar relatório",
      "reason": "Motivo da contestação",
      "status": "Status",
      "clear-filters": "Limpar filtros",
      "filter.seven-days": "Últimos 7 dias",
      "filter.today": "Hoje",
      "filter.yesterday": "Ontem",
      "filter.fifteen-days": "Ultimos 15 dias",
      "filter.last-month": "Ultimo mês",
      "csv.error": "Falha no download do relatório",
      "csv.success": "Relatório baixado",
      "csv.loading": "Gerando relatório"
    },
    "en-US": {
      "search": "Search",
      "batch-dispute": "Batch dispute",
      "placeholder": "Search by ID",
      "download": "Download",
      "reason": "Contestation reason",
      "status": "Status",
      "clear-filters": "Clear filters",
      "filter.seven-days": "Last 7 days",
      "filter.today": "Today",
      "filter.yesterday": "Yesterday",
      "filter.fifteen-days": "Last 15 days",
      "filter.last-month": "Last month",
      "csv.error": "Failed to download the report",
      "csv.success": "Report downloaded",
      "csv.loading": "Generating report"
    }
  }
</i18n>
